import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import RegistryCard from "@/components/windie/RegistryCard";
import { MCPS, PLUGINS, SKILLS } from "@/lib/registryData";

const TABS = [
  { key: "mcps", label: "MCPs", data: MCPS },
  { key: "plugins", label: "Plugins", data: PLUGINS },
  { key: "skills", label: "Skills", data: SKILLS },
];

function EmptyShelf({ label }) {
  return (
    <div className="rounded-sm border border-dashed border-[#e6e4e0] bg-[#f9f8f6] p-14 text-center">
      <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#828c7e]">
        0 {label}
      </p>
      <h3 className="mt-4 font-serif italic text-3xl sm:text-4xl leading-tight tracking-tight text-[#1c1b1a] font-light">
        The shelf is empty — for now.
      </h3>
      <p className="mt-4 max-w-md mx-auto font-sans text-[15px] leading-relaxed text-[#6b6a68]">
        {label[0].toUpperCase() + label.slice(1)} arrive by invitation.
        Building one? We&apos;d love to see it.
      </p>
    </div>
  );
}

export default function Extensions() {
  const [active, setActive] = useState("mcps");
  const activeTab = TABS.find((tab) => tab.key === active);

  return (
    <section
      id="extensions"
      data-testid="extensions-section"
      className="relative py-16 md:py-24 border-t border-[#e6e4e0]"
    >
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative flex flex-col items-center text-center mb-14"
        >
          <div>
            <h2 className="font-serif text-5xl sm:text-6xl md:text-7xl leading-[0.95] tracking-tight text-[#1c1b1a] font-light">
              Extensions <em className="italic">for the runtime.</em>
            </h2>
          </div>
          <div className="mt-8 md:absolute md:right-0 md:bottom-0 md:mt-0">
            <Link
              to="/registry"
              className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-[#1c1b1a] hover:text-[#828c7e] transition-colors"
            >
              Full registry
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </motion.div>

        <div className="flex items-center gap-8 border-b border-[#e6e4e0] mb-10">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              data-testid={`extensions-tab-${tab.key}`}
              onClick={() => setActive(tab.key)}
              className="relative pb-4 font-mono text-[11px] uppercase tracking-[0.22em] text-[#1c1b1a]"
            >
              <span className={active === tab.key ? "opacity-100" : "opacity-50 hover:opacity-100 transition-opacity"}>
                {tab.label} <span className="text-[#828c7e]">· {tab.data.length}</span>
              </span>
              {active === tab.key && (
                <motion.span
                  layoutId="extensions-underline"
                  className="absolute -bottom-px left-0 right-0 h-px bg-[#828c7e]"
                />
              )}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            {activeTab.data.length === 0 ? (
              <EmptyShelf label={activeTab.label.toLowerCase()} />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {activeTab.data.map((item, index) => (
                  <RegistryCard key={item.slug} item={item} index={index} />
                ))}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
